import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { RotateCcw, X } from "lucide-react";

/**
 * DS ChipBox — figma 1993:10020.
 *
 * "N개 선택됨 + 초기화" 헤더와 삭제 가능한 chip 목록을 담는 회색 박스.
 * 선택된 매장 / 제품 / 카테고리 표시에 공용으로 쓴다.
 *
 * 접힘 기준은 chip 개수가 아니라 **3줄**이다 (우진 2026-08-11). 제품명·매장명 길이가
 * 제각각이라 개수로는 줄 수가 안 맞는다. 3줄을 넘길 때만 "전체보기"가 뜬다.
 *
 * @param {object} props
 * @param {Array<{id: string, label: string}>} props.items
 * @param {(id: string) => void} [props.onRemove] - 미전달 시 chip 삭제 버튼 숨김
 * @param {() => void} [props.onReset] - 미전달 시 "초기화" 숨김
 * @param {(count: number) => string} [props.countFormatter]
 */
const DSChipBox = ({
  items = [],
  onRemove,
  onReset,
  countFormatter = (count) => `${count}개 선택됨`,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const listRef = useRef(null);

  // 클램프된 높이보다 실제 내용이 크면 "전체보기"를 노출한다.
  const measure = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    if (expanded) return; // 펼친 상태에서는 이미 링크가 떠 있다 ("접기")
    setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [expanded]);

  useLayoutEffect(measure, [measure, items]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return (
    <div className="ds-chip-box">
      <div className="ds-chip-box__head">
        <span className="ds-chip-box__count">
          {countFormatter(items.length)}
        </span>
        {onReset ? (
          <button
            type="button"
            className="ds-reset-button"
            disabled={items.length === 0}
            onClick={onReset}
          >
            <RotateCcw />
            초기화
          </button>
        ) : null}
      </div>
      <div
        ref={listRef}
        className={[
          "ds-chip-box__list",
          !expanded && "ds-chip-box__list--clamped",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {items.map((item) => (
          <span key={item.id} className="ds-chip-small">
            <span className="ds-chip-small__label" title={item.label}>
              {item.label}
            </span>
            {onRemove ? (
              <button
                type="button"
                className="ds-chip-small__delete"
                aria-label={`${item.label} 삭제`}
                onClick={() => onRemove(item.id)}
              >
                <X />
              </button>
            ) : null}
          </span>
        ))}
      </div>
      {expanded || isOverflowing ? (
        <button
          type="button"
          className="ds-chip-box__more"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "접기" : "전체보기"}
        </button>
      ) : null}
    </div>
  );
};

export default React.memo(DSChipBox);
